import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import BaseLayout from "@/v2/components/BaseLayout";
import ArticleCard from "@/v2/components/ArticleCard";
import { fetchTopicArticles } from "@/v2/queries";
import { AMNEN_LABELS, amneLabel } from "@/v2/taxonomy";
import NotFound from "@/pages/NotFound";

const SITE = "https://korthet.se";

/**
 * Ämneshubb (/amne/:topic).
 * Ger djuplänkning: varje artikel nås inom två klick från startsidan, vilket
 * hjälper sökmotorer att upptäcka och indexera hela arkivet.
 */
export default function TopicPage() {
  const { topic } = useParams<{ topic: string }>();
  const isKnown = !!topic && Object.prototype.hasOwnProperty.call(AMNEN_LABELS, topic);

  const { data, isLoading, error } = useQuery({
    queryKey: ["v2", "topic", topic],
    queryFn: () => fetchTopicArticles(topic!, 40),
    enabled: isKnown,
  });

  if (!isKnown) return <NotFound />;

  const label = amneLabel(topic!);
  const canonicalUrl = `${SITE}/amne/${topic}`;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: `${label} – Korthet`,
      url: canonicalUrl,
      inLanguage: "sv-SE",
      isPartOf: { "@type": "WebSite", name: "Korthet", url: `${SITE}/` },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Start", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "Ämnen", item: `${SITE}/amnen` },
        { "@type": "ListItem", position: 3, name: label, item: canonicalUrl },
      ],
    },
  ];

  return (
    <BaseLayout
      title={`${label} – nyheter i korthet`}
      description={`Alla nyheter om ${label.toLowerCase()}, sammanfattade av AI.`}
      canonicalPath={`/amne/${topic}`}
      jsonLd={jsonLd}
    >
      <h1 className="k2-h1">{label}</h1>
      <p className="k2-lead">
        Senaste nyheterna om {label.toLowerCase()}. <Link to="/amnen">Alla ämnen</Link>
      </p>
      {isLoading && <p className="k2-loading">Laddar…</p>}
      {error && <p className="k2-error">Kunde inte hämta artiklar.</p>}
      {data && data.length === 0 && <p className="k2-empty">Inga artiklar om {label.toLowerCase()} ännu.</p>}
      {data && data.length > 0 && (
        <ul className="k2-feed">
          {data.map((a) => (
            <ArticleCard key={a.id} article={a} />
          ))}
        </ul>
      )}
    </BaseLayout>
  );
}
